import { ReactNode } from "react";
import { usePermissions } from "../../hooks/usePermissions";

interface CanProps {
  /** Single permission. Use this OR `anyOf` / `allOf`, not multiple. */
  permission?: string;
  /** OR — show children if the user has ANY of these. */
  anyOf?: string[];
  /** AND — show children only if the user has ALL of these. */
  allOf?: string[];
  /** Rendered while the user-info fetch is still pending. Default: null. */
  loading?: ReactNode;
  /** Rendered when the user lacks the permission. Default: null. */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Declarative permission gate for JSX.
 *
 *   <Can permission="campaigns:view">
 *     <Link to="/campaigns">Campaigns</Link>
 *   </Can>
 *
 *   <Can anyOf={["users:edit", "users:delete"]} fallback={<DisabledTooltip />}>
 *     <EditUserButton />
 *   </Can>
 *
 *   <Can allOf={["templates:view", "templates:edit"]}>
 *     <TemplateEditor />
 *   </Can>
 */
export default function Can({
  permission,
  anyOf,
  allOf,
  loading = null,
  fallback = null,
  children,
}: CanProps) {
  const { can, canAny, canAll, isReady } = usePermissions();

  if (!isReady) return <>{loading}</>;

  const allowed = permission
    ? can(permission)
    : anyOf
      ? canAny(anyOf)
      : allOf
        ? canAll(allOf)
        : true; // no permission specified → always allow

  return <>{allowed ? children : fallback}</>;
}
